// src/pages/Login.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import api, { setAuthStorage, setTokens } from '../api';
import { resolveTenantHint } from '../utils/tenant';
import TenantOnboard from './TenantOnboard.jsx';

/** Lightweight inline Modal (no external deps) */
function Modal({ open, onClose, title, children }) {
  const onKey = useCallback((e) => { if (e.key === 'Escape') onClose?.(); }, [onClose]);

  useEffect(() => {
    if (!open) return;
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onKey]);

  if (!open) return null;

  return (
    <div className="modal-host" role="dialog" aria-modal="true" aria-labelledby="modal-title">
      <div className="modal-backdrop" onClick={onClose} />
      <div className="modal-panel" role="document">
        <div className="modal-head">
          <h2 id="modal-title" className="modal-title">{title || 'Dialog'}</h2>
          <button className="modal-close" onClick={onClose} aria-label="Close">✕</button>
        </div>
        <div className="modal-body">{children}</div>
      </div>
    </div>
  );
}

export default function Login() {
  const [tenant, setTenant] = useState(localStorage.getItem('tenantSlug') || '');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(true);
  const [err, setErr] = useState(null); // { text, kind } | null
  const [submitting, setSubmitting] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [showOnboard, setShowOnboard] = useState(false);
  const [logoBroken, setLogoBroken] = useState(false);
  const [mfaOpen, setMfaOpen] = useState(false);
  const [mfaToken, setMfaToken] = useState('');
  const [mfaMethods, setMfaMethods] = useState([]);
  const [mfaMethod, setMfaMethod] = useState('totp');
  const [mfaCode, setMfaCode] = useState('');
  const [mfaError, setMfaError] = useState('');
  const [mfaSending, setMfaSending] = useState(false);
  const [mfaVerifying, setMfaVerifying] = useState(false);
  const [rememberDevice, setRememberDevice] = useState(true);
  const [mfaEmailHint, setMfaEmailHint] = useState('');
  const [mfaRetrySec, setMfaRetrySec] = useState(0);

  const formRef = useRef(null);
  const dismissTimer = useRef(null);
  const hoveringAlert = useRef(false);

  useEffect(() => {
    if (!tenant) {
      const guess = resolveTenantHint();
      if (guess) setTenant(guess);
    }
  }, []);

  // Auto-dismiss alert after 5s (pauses while hovered)
  useEffect(() => {
    if (!err) return;
    if (dismissTimer.current) clearTimeout(dismissTimer.current);
    dismissTimer.current = setTimeout(() => {
      if (!hoveringAlert.current) setErr(null);
    }, 5000);
    return () => dismissTimer.current && clearTimeout(dismissTimer.current);
  }, [err]);

  useEffect(() => {
    if (mfaRetrySec <= 0) return;
    const id = setInterval(() => {
      setMfaRetrySec((s) => (s > 0 ? s - 1 : 0));
    }, 1000);
    return () => clearInterval(id);
  }, [mfaRetrySec]);

  useEffect(() => {
    if (mfaRetrySec === 0 && mfaError) {
      setMfaError('');
    }
  }, [mfaRetrySec, mfaError]);

  useEffect(() => {
    if (!mfaOpen) setMfaRetrySec(0);
  }, [mfaOpen]);

  function friendlyMessage(e) {
    const status = e?.response?.status;
    const apiMsg = e?.response?.data?.message || '';

    // Explicit mappings
    if (status === 400 || status === 401) return { text: 'Incorrect email or password. Please try again.', kind: 'error' };
    if (status === 404) return { text: 'The shop or organisation name was not found. Please register.', kind: 'error' };
    if (status === 422) return { text: 'Please check the details and try again.', kind: 'error' };
    if (status === 500) return { text: 'The shop or organisation name was not found. Please register.', kind: 'error' };

    // Network-level: no response but a request exists (CORS, offline, timeout, DNS, server down)
    if (!e?.response && e?.request) {
      return { text: 'Cannot reach the server right now. Please check your connection or try again later.', kind: 'warn' };
    }

    // Axios “Network Error” string or backend hint
    const lower = String(apiMsg).toLowerCase();
    if (e?.message?.toLowerCase().includes('network')) {
      return { text: 'We’re having trouble connecting. Please check your internet and try again.', kind: 'warn' };
    }
    if (lower.includes('tenant') || lower.includes('shop') || lower.includes('organisation') || lower.includes('organization')) {
      return { text: 'The shop or organisation name was not found. Please register.', kind: 'error' };
    }
    if (lower.includes('credential') || lower.includes('password') || lower.includes('invalid login')) {
      return { text: 'Incorrect email or password. Please try again.', kind: 'error' };
    }

    return { text: 'Something went wrong. Please try again in a moment.', kind: 'warn' };
  }

  function triggerShake() {
    const el = formRef.current;
    if (!el) return;
    el.classList.remove('shake');
    // restart animation
    // eslint-disable-next-line no-unused-expressions
    el.offsetHeight;
    el.classList.add('shake');
    setTimeout(() => el.classList.remove('shake'), 500);
  }

  function maskEmail(addr = '') {
    const [user, domain] = String(addr).split('@');
    if (!user || !domain) return '';
    const maskedUser = user.length <= 2 ? `${user[0] || ''}*` : `${user[0]}***${user[user.length - 1]}`;
    return `${maskedUser}@${domain}`;
  }

  function applyAuthTokens(data) {
    const token   = data?.token || data?.access_token || data?.accessToken || data?.jwt || data?.id_token || data?.session?.token || data?.auth?.token || null;
    const refresh = data?.refresh || data?.refresh_token || data?.refreshToken || data?.session?.refresh || data?.auth?.refresh || null;
    const userObj = data?.user || data?.account || data?.profile || null;
    const perms   = data?.permissions || data?.perms || data?.scopes || data?.capabilities || null;
    const role    = data?.role || data?.user_role || userObj?.role || null;

    const store = remember ? localStorage : sessionStorage;
    try {
      for (const k of ['token','access_token','refresh','refresh_token','user','auth_user','userInfo']) {
        localStorage.removeItem(k);
        sessionStorage.removeItem(k);
      }
      if (userObj || role || perms) {
        const merged = { ...(userObj || {}), role: role ?? userObj?.role, permissions: perms ?? userObj?.permissions };
        const json = JSON.stringify(merged);
        store.setItem('user', json);
        store.setItem('auth_user', json);
        store.setItem('userInfo', json);
      }
    } catch { /* ignore storage errors */ }

    try { setTokens && setTokens(token, refresh); } catch {}
  }

  function openMfaChallenge(data) {
    const token = data?.mfa_token || '';
    const methods = Array.isArray(data?.mfa_methods) ? data.mfa_methods : [];
    const emailHint = maskEmail(data?.user?.email || email);
    const defaultMethod = methods.includes('totp') ? 'totp' : (methods.includes('email') ? 'email' : 'totp');

    setMfaToken(token);
    setMfaMethods(methods);
    setMfaMethod(defaultMethod);
    setMfaCode('');
    setMfaError('');
    setMfaEmailHint(emailHint);
    setRememberDevice(true);
    setMfaOpen(true);
  }

  async function sendMfaEmailCode() {
    if (!mfaToken) return;
    setMfaSending(true);
    setMfaError('');
    try {
      const hint = resolveTenantHint(tenant);
      await api.post('/auth/mfa/email/send', { mfa_token: mfaToken }, { headers: { 'X-Tenant-ID': hint } });
    } catch (e) {
      const retry = Number(e?.response?.data?.retry_after_seconds || 0);
      if (retry > 0) setMfaRetrySec(retry);
      const msg = e?.response?.data?.error || 'Failed to send code';
      setMfaError(msg);
    } finally {
      setMfaSending(false);
    }
  }

  async function verifyMfaCode(e) {
    e?.preventDefault?.();
    if (!mfaCode.trim()) {
      setMfaError('Enter the verification code');
      return;
    }
    setMfaVerifying(true);
    setMfaError('');
    try {
      const hint = resolveTenantHint(tenant);
      const { data } = await api.post(
        '/auth/mfa/verify',
        {
          mfa_token: mfaToken,
          code: mfaCode.trim(),
          method: mfaMethod,
          remember_device: rememberDevice,
        },
        { headers: { 'X-Tenant-ID': hint } }
      );

      if (data?.trustedDeviceToken) {
        try { localStorage.setItem('mfa_trusted_device', data.trustedDeviceToken); } catch {}
      }
      applyAuthTokens(data);
      setMfaOpen(false);
      window.location.assign('/dashboard');
    } catch (e2) {
      const msg = e2?.response?.data?.error || 'Verification failed';
      setMfaError(msg);
    } finally {
      setMfaVerifying(false);
    }
  }

  async function submit(e) {
    e.preventDefault();

    if (!email.trim() || !password.trim()) {
      setErr({ text: 'Email and password are required.', kind: 'error' });
      triggerShake();
      return;
    }

    const hint = resolveTenantHint(tenant);
    if (!hint) {
      setErr({ text: 'Shop / organisation name is required.', kind: 'error' });
      triggerShake();
      return;
    }

    // 🚫 Strict case-sensitive check — must match lowercase slug pattern
    if (hint !== hint.toLowerCase()) {
      setErr({ text: 'The shop or organisation name was not found. Please register.', kind: 'error' });
      triggerShake();
      return;
    }

    // Optional: Ensure only valid slug characters (a-z, 0-9, -)
    if (!/^[a-z0-9-]+$/.test(hint)) {
      setErr({ text: 'The shop or organisation name was not found. Please register.', kind: 'error' });
      triggerShake();
      return;
    }

    localStorage.setItem('tenantSlug', hint);
    setErr(null);
    setSubmitting(true);

    try {
      const trustedDevice = localStorage.getItem('mfa_trusted_device') || '';
      setAuthStorage(remember ? 'local' : 'session');
      const { data } = await api.post(
        '/auth/login',
        { email, password, remember, tenant: hint },
        {
          headers: {
            'X-Tenant-ID': hint,
            ...(trustedDevice ? { 'X-Trusted-Device': trustedDevice } : {}),
          },
        }
      );

      if (data?.mfa_required) {
        openMfaChallenge(data);
        return;
      }

      applyAuthTokens(data);
      // Prefer dashboard; ProfileGate will route to /welcome if shop profile is incomplete.
      window.location.assign('/dashboard');
    } catch (e2) {
      const fm = friendlyMessage(e2);
      setErr(fm);
      triggerShake();
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="login-wrap">
      <div className="login-card" role="dialog" aria-labelledby="loginTitle">

        {/* unified company logo (public/brand/logo.png) */}
        {!logoBroken ? (
          <img
            src="/brand/logo.png"
            alt="Company logo"
            className="brand-logo"
            onError={() => setLogoBroken(true)}
            draggable="false"
          />
        ) : (
          <div className="logo-fallback" aria-hidden="true" />
        )}

        <h1 id="loginTitle">Sign in</h1>

        {/* Friendly alert */}
        {err && (
          <div
            className={`alert ${err.kind === 'warn' ? 'alert-warn' : 'alert-error'}`}
            role="status"
            aria-live="polite"
            onMouseEnter={() => { hoveringAlert.current = true; }}
            onMouseLeave={() => { hoveringAlert.current = false; }}
          >
            <div className="alert-icon" aria-hidden="true">
              {err.kind === 'warn' ? '⚠️' : '⛔'}
            </div>
            <div className="alert-text">{err.text}</div>
            <button className="alert-close" onClick={() => setErr(null)} aria-label="Dismiss">✕</button>
          </div>
        )}

        <form onSubmit={submit} className="form" ref={formRef}>
          <label className="label">Shop / Organisation Name</label>
          <input
            value={tenant}
            onChange={(e)=>setTenant(e.target.value)}
            className="input"
            placeholder="e.g. anvi-electronics"
            required
          />

          <label className="label">Email</label>
          <input
            type="email"
            value={email}
            onChange={(e)=>setEmail(e.target.value)}
            className="input"
            placeholder="you@example.com"
            autoFocus
            required
          />

          <label className="label">Password</label>
          <div className="pwd-wrap">
            <input
              type={showPwd ? 'text' : 'password'}
              value={password}
              onChange={(e)=>setPassword(e.target.value)}
              className="input"
              required
            />
            <button
              type="button"
              className="eye"
              onClick={()=>setShowPwd(v=>!v)}
              aria-label={showPwd ? 'Hide password' : 'Show password'}
            >
              {showPwd ? '🙈' : '👁️'}
            </button>
          </div>

          <label className="check">
            <input type="checkbox" checked={remember} onChange={(e)=>setRemember(e.target.checked)} />
            <span>Remember me</span>
          </label>

          <button type="submit" className="btn primary" disabled={submitting}>
            {submitting ? 'Signing in…' : 'Sign In'}
          </button>
        </form>

        <div className="footer">
          <a className="link" href="/forgot">Forgot password?</a>
          <button
            type="button"
            className="link link-btn"
            onClick={() => setShowOnboard(true)}
            aria-haspopup="dialog"
            aria-expanded={showOnboard ? 'true' : 'false'}
          >
            Signup
          </button>
        </div>
      </div>

      <Modal open={showOnboard} onClose={() => setShowOnboard(false)} title="Register your Shop / Organisation">
        <TenantOnboard embedded onDone={() => setShowOnboard(false)} onCancel={() => setShowOnboard(false)} />
      </Modal>

      <Modal open={mfaOpen} onClose={() => setMfaOpen(false)} title="Verify your sign-in">
        <form onSubmit={verifyMfaCode} className="mfa-form">
          <p className="mfa-subtitle">Enter your verification code to continue.</p>

          <div className="mfa-methods">
            {mfaMethods.includes('totp') && (
              <label className="mfa-choice">
                <input
                  type="radio"
                  name="mfa-method"
                  value="totp"
                  checked={mfaMethod === 'totp'}
                  onChange={() => setMfaMethod('totp')}
                />
                <span>Authenticator app</span>
              </label>
            )}
            {mfaMethods.includes('email') && (
              <label className="mfa-choice">
                <input
                  type="radio"
                  name="mfa-method"
                  value="email"
                  checked={mfaMethod === 'email'}
                  onChange={() => setMfaMethod('email')}
                />
                <span>Email code {mfaEmailHint ? `(${mfaEmailHint})` : ''}</span>
              </label>
            )}
          </div>

          {mfaMethod === 'email' && (
            <div className="mfa-row">
              <button
                type="button"
                className="btn outline"
                onClick={sendMfaEmailCode}
                disabled={mfaSending || mfaRetrySec > 0}
              >
                {mfaSending ? 'Sending...' : (mfaRetrySec > 0 ? `Send code (${mfaRetrySec}s)` : 'Send code')}
              </button>
              <span className="mfa-note">Check your inbox for the 6-digit code.</span>
            </div>
          )}

          <label className="label">Verification code</label>
          <input
            value={mfaCode}
            onChange={(e) => setMfaCode(e.target.value)}
            className="input"
            placeholder="123456"
            inputMode="numeric"
            autoFocus
          />

          <label className="check">
            <input type="checkbox" checked={rememberDevice} onChange={(e) => setRememberDevice(e.target.checked)} />
            <span>Remember this device</span>
          </label>

          {mfaError && (
            <div className="alert alert-error mfa-error">
              {mfaRetrySec > 0 ? `${mfaError} (${mfaRetrySec}s)` : mfaError}
            </div>
          )}

          <div className="row end" style={{ marginTop: 12, gap: 8 }}>
            <button type="button" className="btn outline" onClick={() => setMfaOpen(false)}>Cancel</button>
            <button type="submit" className="btn primary" disabled={mfaVerifying}>
              {mfaVerifying ? 'Verifying...' : 'Verify'}
            </button>
          </div>
        </form>
      </Modal>

      <style>{`
        .login-wrap {
          min-height: calc(100dvh - 64px);
          display: grid; place-items: center;
          padding: 16px; overflow: hidden; background: transparent;
        }
        @media (min-width: 640px) { .login-wrap { padding: 24px; } }

        .login-card {
          width: 100%; max-width: 420px; background: var(--bg-elev, #fff);
          border: 1px solid var(--hairline, #e5e7eb);
          border-radius: 16px; box-shadow: var(--shadow, 0 10px 30px rgba(2,6,23,.08));
          padding: 20px;
        }

        /* Logo */
        .brand-logo {
          display: block;
          width: 200px;
          height: 200px;
          object-fit: contain;
          margin: 0 auto 16px;
          border-radius: 28px;
          user-select: none;
          pointer-events: none;
          background: transparent;
          border: none;
          box-shadow: none;
          filter: none;
        }
        .logo-fallback {
          width: 64px; height: 64px; border-radius: 14px; margin: 0 auto 12px;
          background: radial-gradient(80% 80% at 30% 20%, #93c5fd, #60a5fa);
          box-shadow: 0 6px 18px rgba(37,99,235,0.28);
        }

        .login-card h1 { margin: 0 0 10px; font-size: 20px; line-height: 1.2; text-align: center; }

        /* Alerts */
        .alert {
          position: relative;
          display: grid;
          grid-template-columns: 24px 1fr auto;
          align-items: start;
          gap: 10px;
          padding: 10px 12px;
          border-radius: 10px;
          margin: 10px 0 6px 0;
          border: 1px solid transparent;
          animation: fadeIn .15s ease-out;
        }
        .alert-error {
          background: #fff1f2;            /* rose-50 */
          border-color: #fecdd3;          /* rose-200 */
          color: #9f1239;                 /* rose-800 */
        }
        .alert-warn {
          background: #fffbeb;            /* amber-50 */
          border-color: #fde68a;          /* amber-300 */
          color: #92400e;                 /* amber-800 */
        }
        .alert-icon { line-height: 1; font-size: 18px; margin-top: 2px; }
        .alert-text { font-size: 14px; }
        .alert-close {
          align-self: center;
          background: transparent; border: 0; cursor: pointer;
          font-size: 16px; line-height: 1; opacity: .7;
        }
        .alert-close:hover { opacity: 1; }

        .form { display: grid; gap: 10px; margin-top: 6px; }
        .label { font-size: 12px; color: var(--muted, #6b7280); }
        .input { width: 100%; height: 40px; border-radius: 10px; border: 1px solid var(--hairline, #e5e7eb); background: var(--bg, #fff); color: var(--text, #0f172a); padding: 0 12px; }
        .pwd-wrap { position: relative; }
        .pwd-wrap .input { padding-right: 40px; }
        .eye { position: absolute; top: 50%; right: 6px; transform: translateY(-50%); background: transparent; border: none; cursor: pointer; font-size: 16px; line-height: 1; }
        .check { display: inline-flex; align-items: center; gap: 8px; margin-top: 2px; user-select: none; }
        .check input { width: 16px; height: 16px; }
        .btn.primary { margin-top: 6px; width: 100%; height: 40px; border-radius: 10px; border: 0; background: var(--link, #2563eb); color: #fff; font-weight: 600; cursor: pointer; }
        .btn.primary:disabled { opacity: .7; cursor: not-allowed; }

        .footer { margin-top: 10px; display: flex; justify-content: space-between; align-items: center; }
        .link { color: var(--link, #2563eb); text-decoration: none; font-size: 14px; background: none; border: none; padding: 0; }
        .link:hover { text-decoration: underline; }
        .link-btn { cursor: pointer; }

        html, body, #root { max-width: 100%; overflow-x: hidden; }

        /* Subtle shake for failed submit */
        @keyframes shakeX {
          0%,100% { transform: translateX(0) }
          20% { transform: translateX(-6px) }
          40% { transform: translateX(6px) }
          60% { transform: translateX(-4px) }
          80% { transform: translateX(4px) }
        }
        .shake { animation: shakeX .35s ease; }
        @keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }

        /* ---- Modal styles ---- */
        .modal-host { position: fixed; inset: 0; z-index: 50; display: grid; place-items: center; }
        .modal-backdrop { position: absolute; inset: 0; background: rgba(0,0,0,.5); backdrop-filter: blur(4px); }
        .modal-panel { position: relative; width: min(92vw, 720px); max-height: 90vh; display: flex; flex-direction: column; background: var(--bg-elev, #101014); color: var(--fg, #e6e6f0); border: 1px solid var(--hairline, #2a2a2e); border-radius: 16px; box-shadow: 0 10px 30px rgba(0,0,0,.35); overflow: hidden; }
        .modal-head { display: flex; align-items: center; justify-content: space-between; padding: 12px 16px; border-bottom: 1px solid var(--hairline, #2a2a2e); background: var(--bg-elev, #101014); }
        .modal-title { margin: 0; font-size: 16px; font-weight: 600; }
        .modal-close { background: transparent; border: 0; cursor: pointer; padding: 6px 8px; border-radius: 8px; }
        .modal-close:hover { background: rgba(255,255,255,.06); }
        .modal-body { padding: 16px; overflow: auto; background: var(--bg, #0f1222); }

        /* MFA modal */
        .mfa-form { display: grid; gap: 10px; }
        .mfa-subtitle { margin: 0; font-size: 14px; opacity: 0.8; }
        .mfa-methods { display: grid; gap: 6px; margin: 6px 0 4px; }
        .mfa-choice { display: flex; align-items: center; gap: 8px; font-size: 14px; }
        .mfa-row { display: flex; align-items: center; gap: 10px; }
        .mfa-note { font-size: 12px; opacity: 0.8; }
        .mfa-error { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
      `}</style>
    </div>
  );
}
